<template>
  <app-drawer :model-value="subscriptionPaneIsOpen"
              size="sm"
              :persistent="false"
              data-locator="subscription-teams-drawer"
              :ok-text="isUpdating ? $t('common.register') : $t('subscriptions.views.SubscriptionTeams.createTheTeam')"
              :ok-disabled="okDisabled || isPending"
              :ok-loading="isPending"
              :cancel-disabled="isPending"
              @update:model-value="closePane"
              @cancel="closePane"
              @ok="onSubmit"
  >
    <template #title>{{ drawerTitle }}</template>

    <template #body>
      <div class="d-flex flex-column ga-4">
        <div class="d-flex flex-column ga-2">
          <p class="mb-0 text--small text--bold text-text-primary">
            {{ $t('subscriptions.views.SubscriptionTeams.teamName') }}
          </p>
          <!--
            Limit is 100 only to set a reasonable limit based on past inputs with the same use-case
            there was no spec given by the backend
          -->
          <AppTextField v-model="name"
                        :placeholder="$t('subscriptions.views.SubscriptionTeams.newTeam')"
                        counter="100"
                        maxlength="100"
                        data-locator="team-name-input"
          />
        </div>

        <div class="d-flex flex-column ga-2">
          <p class="mb-0 text--small text--bold text-text-primary">
            {{ $t('subscriptions.views.SubscriptionTeams.addMembers') }}
          </p>
          <SearchTextField v-model="searchQuery"
                           :tooltipText="$t('project.checklist.dialogs.SaveChecklistDialog.searchMember')"
                           fluid
          />
        </div>

        <div v-if="getSelfContactListPending" class="text-center">
          <AppLoader small/>
        </div>
        <div v-else class="d-flex flex-column ga-4">
          <v-checkbox v-for="user in sortedComputedSubscriptionUsers"
                      :key="`user-${user.id}`"
                      v-model="selectedUsers"
                      :value="user.id"
                      class="my-0"
                      hide-details
                      :data-locator="`team-member-${user.id}-checkbox`"
          >
            <template #label>
              <AddMemberFormComboboxItem class="pl-1" :item="user" :withCompany="false" />
            </template>
          </v-checkbox>
        </div>
      </div>
    </template>

    <template v-if="isUpdating" #footer-link>
      <app-button type="outlined"
                  variant="danger"
                  :disabled="isPending"
                  data-locator="delete-team-button"
                  @click="showDeleteTeamDialog"
      >
        {{ $t('subscriptions.views.SubscriptionTeams.deleteTeam') }}
      </app-button>
    </template>
  </app-drawer>
</template>

<script>
  import { mapMutations, mapState, mapActions } from 'vuex'

  import AppLoader from '@/common/AppLoader.vue'
  import AppTextField from '@/common/AppTextField.vue'
  import SearchTextField from '@/common/SearchTextField.vue'
  import { toast } from '@/common/utils/toast'
  import AddMemberFormComboboxItem from '@/project/members/AddMemberFormComboboxItem.vue'
  import {
    POST_SUBSCRIPTION_TEAM,
    PATCH_SUBSCRIPTION_TEAM,
    GET_SUBSCRIPTION_TEAMS,
  } from '@/store/modules/subscription/action_types'
  import { SET_DELETE_TEAM_DIALOG } from '@/store/modules/subscription/mutation_types'
  import { GET_SELF_CONTACT_LIST } from '@/store/modules/user/action_types'

  export default {
    name: 'SubscriptionTeamsPane',
    components: {
      AppTextField,
      AppLoader,
      SearchTextField,
      AddMemberFormComboboxItem,
    },
    emits: ['close'],
    data () {
      return {
        name: '',
        searchQuery: '',
        selectedUsers: [],
      }
    },
    computed: {
      ...mapState('subscription', [
        'subscriptionPaneIsOpen',
        'postSubscriptionTeamPending',
        'patchSubscriptionTeamPending',
        'selectedSubscriptionTeam',
      ]),
      ...mapState('user', ['profile', 'selfContactListFromSubscriptionPayingUsers', 'getSelfContactListPending']),
      drawerTitle () {
        return this.isUpdating
          ? `${this.$t('common.modify')} ${this.name}`
          : this.$t('subscriptions.views.SubscriptionTeams.createTeam')
      },
      computedSubscriptionUsers () {
        const users = this.selfContactListFromSubscriptionPayingUsers
        if (this.searchQuery && users) {
          return users.filter(user => {
            const fullName = user.firstName + ' ' + user.lastName
            return fullName.toLowerCase().includes(this.searchQuery.toLowerCase())
          })
        }
        return users
      },
      sortedComputedSubscriptionUsers () {
        const selectedUsersArray = []
        const otherUsersArray = []
        this.computedSubscriptionUsers.forEach(user => {
          if (this.selectedUsers.includes(user.id)) {
            selectedUsersArray.push(user)
          } else {
            otherUsersArray.push(user)
          }
        })
        return selectedUsersArray.concat(otherUsersArray)
      },
      isUpdating () {
        return this.selectedSubscriptionTeam !== null
      },
      okDisabled () {
        return this.name === '' || this.selectedUsers.length === 0
      },
      isPending () {
        return this.postSubscriptionTeamPending || this.patchSubscriptionTeamPending
      },
    },
    watch: {
      subscriptionPaneIsOpen: {
        handler (value) {
          if (value) {
            // The drawer is no longer mounted and destroyed around each opening, so the form
            // has to be emptied by hand before the selected team is applied: otherwise the
            // team edited last would leak into the next creation.
            this.name = ''
            this.searchQuery = ''
            this.selectedUsers = []
            this.applySelectedSubscriptionTeamData()
            this.prepareGetSubscriptionUsers()
          }
        },
        immediate: true,
      },
    },
    methods: {
      ...mapActions('user', [GET_SELF_CONTACT_LIST]),
      ...mapActions('subscription', [
        POST_SUBSCRIPTION_TEAM,
        PATCH_SUBSCRIPTION_TEAM,
        GET_SUBSCRIPTION_TEAMS,
      ]),
      // The pane-open flag and the selected team both belong to the parent, which owns their
      // mutations: this component only reports that it wants to close.
      ...mapMutations('subscription', [
        SET_DELETE_TEAM_DIALOG,
      ]),
      applySelectedSubscriptionTeamData () {
        if (this.selectedSubscriptionTeam) {
          this.name = this.selectedSubscriptionTeam.name
          this.selectedUsers = this.selectedSubscriptionTeam.members.map(item => item.id)
        }
      },
      async prepareGetSubscriptionUsers () {
        try {
          await this.GET_SELF_CONTACT_LIST({ fromUserSubscriptionOnly: true, payingUsersOnly: true })
        } catch (error) {
          console.error(error)
          toast.error(this.$t('common.subscription.errors.getSubscriptionUsersError'))
        }
      },
      onSubmit () {
        if (this.isUpdating) {
          this.preparePatchSubscriptionTeam()
        } else {
          this.preparePostSubscriptionTeam()
        }
      },
      async preparePostSubscriptionTeam () {
        try {
          const data = {
            name: this.name,
            memberIds: this.selectedUsers,
          }
          await this.POST_SUBSCRIPTION_TEAM(data)
          this.getSubscriptionTeams()
          this.closePane()

          toast.success(this.$t('subscriptions.views.SubscriptionTeams.saveTeamSuccess', { name: this.name }))
        } catch (e) {
          toast.error(this.$t('subscriptions.views.SubscriptionTeams.saveTeamError'))
        }
      },
      async preparePatchSubscriptionTeam () {
        try {
          const data = {
            name: this.name,
            memberIds: this.selectedUsers,
          }
          await this.PATCH_SUBSCRIPTION_TEAM({
            teamId: this.selectedSubscriptionTeam.id,
            data,
          })
          this.getSubscriptionTeams()
          this.closePane()
          toast.success(this.$t('subscriptions.views.SubscriptionTeams.updateTeamSuccess'))
        } catch (e) {
          toast.error(this.$t('subscriptions.views.SubscriptionTeams.updateTeamError'))
        }
      },
      async getSubscriptionTeams () {
        try {
          await this.GET_SUBSCRIPTION_TEAMS()
        } catch (error) {
          toast.error(this.$t('subscriptions.views.SubscriptionTeams.getTeamsError'))
        }
      },
      showDeleteTeamDialog () {
        this.SET_DELETE_TEAM_DIALOG({
          subscriptionTeam: this.selectedSubscriptionTeam,
          isOpen: true,
        })
        this.closePane()
      },
      closePane () {
        this.$emit('close')
      },
    },
  }
</script>
